/**
 * ToggleButtonGroup Component
 *
 * A reusable toggle button group for single-select options.
 * Supports different color variants for selected state styling.
 */

import { type CSSProperties } from 'react';

/**
 * Color variants for selected button state
 */
export type ToggleButtonVariant = 'primary' | 'success' | 'warning' | 'info';

/**
 * Option configuration for toggle buttons
 */
export interface ToggleButtonOption<T extends string> {
  /** The value of this option */
  value: T;
  /** Display label for the button */
  label: string;
  /** Color variant when selected (defaults to 'primary') */
  variant?: ToggleButtonVariant;
}

/**
 * Props for ToggleButtonGroup component
 */
interface ToggleButtonGroupProps<T extends string> {
  /** Array of options to display */
  options: ToggleButtonOption<T>[];
  /** Currently selected value */
  value: T;
  /** Called when selection changes */
  onChange: (value: T) => void;
  /** Whether the buttons are disabled */
  disabled?: boolean;
  /** Accessible name for the group */
  ariaLabel?: string;
}

/**
 * ToggleButtonGroup provides single-select toggle buttons with consistent styling
 *
 * @example
 * ```tsx
 * <ToggleButtonGroup
 *   options={[
 *     { value: 'send', label: 'Send', variant: 'success' },
 *     { value: 'attempt', label: 'Attempt', variant: 'warning' },
 *   ]}
 *   value={outcome}
 *   onChange={setOutcome}
 * />
 * ```
 */
export const ToggleButtonGroup = <T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: ToggleButtonGroupProps<T>) => {
  return (
    <div style={containerStyle} role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => {
        const isSelected = value === option.value;
        const variant = option.variant ?? 'primary';

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(option.value)}
            disabled={disabled}
            style={{
              ...baseButtonStyle,
              ...(isSelected ? getSelectedStyle(variant) : unselectedStyle),
              ...(disabled ? disabledStyle : {}),
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Get selected style based on variant
 */
const getSelectedStyle = (variant: ToggleButtonVariant): CSSProperties => {
  switch (variant) {
    case 'success':
      return {
        backgroundColor: '#dcfce7',
        borderColor: '#22c55e',
        color: '#166534',
      };
    case 'warning':
      return {
        backgroundColor: '#fef3c7',
        borderColor: '#f59e0b',
        color: '#92400e',
      };
    case 'info':
      return {
        backgroundColor: '#dbeafe',
        borderColor: '#3b82f6',
        color: '#1d4ed8',
      };
    case 'primary':
    default:
      return {
        backgroundColor: '#dbeafe',
        borderColor: '#3b82f6',
        color: '#1d4ed8',
      };
  }
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const baseButtonStyle: CSSProperties = {
  flex: 1,
  padding: '12px 16px',
  fontSize: '1rem',
  fontWeight: 500,
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  minHeight: '44px',
  minWidth: '44px',
};

const unselectedStyle: CSSProperties = {
  backgroundColor: 'transparent',
  borderColor: 'var(--color-border, #e5e7eb)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const disabledStyle: CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

export default ToggleButtonGroup;
